import {AxiosResponse} from 'axios';
import {PlaylistType} from '../backend/enums/playlist-type';
import {Playlist} from '../backend/interfaces/playlist';
import {PlaylistItem} from '../backend/interfaces/playlist-item';
import {PostPlaylist} from '../backend/requests/post-playlist';
import {PostPlaylistItem} from '../backend/requests/post-playlist-item';
import {PutPlaylist} from '../backend/requests/put-playlist';
import {PutPlaylistItem} from '../backend/requests/put-playlist-item';
import {PlaylistEntity} from '../backend/responses/playlist-entity';
import {PlaylistItemEntity} from '../backend/responses/playlist-item-entity';
import {PlaylistItemUser} from '../backend/responses/playlist-item-user';
import {PlaylistUser} from '../backend/responses/playlist-user';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class PlaylistsRepository {
  // Singleton Instance
  private static _instance: PlaylistsRepository;

  // Private
  private logger: Logger;

  static get instance(): PlaylistsRepository {
    if (!this._instance) {
      this._instance = new PlaylistsRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('PlaylistsRepository');
  }

  // Create a playlist
  public async createPlaylist(body: PostPlaylist): Promise<Playlist> {
    // Create the playlist
    const response: AxiosResponse<Playlist> = await BackendApiManager.instance.post<Playlist>(
      '/playlists',
      body
    );

    // Return
    return response.data;
  }

  // Create a playlist item
  public async createPlaylistItem(
    playlistId: string,
    body: PostPlaylistItem
  ): Promise<PlaylistItem> {
    // Create the playlist item
    const response: AxiosResponse<PlaylistItem> =
      await BackendApiManager.instance.post<PlaylistItem>(`/playlists/${playlistId}/items`, body);

    // Return
    return response.data;
  }

  // Update a playlist
  public async updatePlaylistById(playlistId: string, body: PutPlaylist): Promise<Playlist> {
    // Update the playlist
    const response: AxiosResponse<Playlist> = await BackendApiManager.instance.put<Playlist>(
      `/playlists/${playlistId}`,
      body
    );

    // Return
    return response.data;
  }

  // Update a playlist item
  public async updatePlaylistItemById(
    playlistId: string,
    playlistItemId: string,
    body: PutPlaylistItem
  ): Promise<PlaylistItem> {
    // Update the playlist item
    const response: AxiosResponse<PlaylistItem> =
      await BackendApiManager.instance.put<PlaylistItem>(
        `/playlists/${playlistId}/items/${playlistItemId}`,
        body
      );

    // Return
    return response.data;
  }

  // Delete a playlist
  public async deletePlaylistById(playlistId: string): Promise<void> {
    // Delete the playlist
    await BackendApiManager.instance.delete<void>(`/playlists/${playlistId}`);
  }

  // Delete a playlist item
  public async deletePlaylistItemById(playlistId: string, playlistItemId: string): Promise<void> {
    // Delete the playlist item
    await BackendApiManager.instance.delete<void>(
      `/playlists/${playlistId}/items/${playlistItemId}`
    );
  }

  // Get playlist by id as a system administrator
  public async getPlaylistByIdAsSystemAdministrator(
    playlistId: string,
    localize?: boolean
  ): Promise<Playlist> {
    // Set url
    let url: string = `/playlists/${playlistId}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `?localize=${localize}`;
    }

    // Get playlist from backend
    const response: AxiosResponse<Playlist> = await BackendApiManager.instance.get<Playlist>(url);

    // Return
    return response.data;
  }

  // Get playlist item by id as a system administrator
  public async getPlaylistItemByIdAsSystemAdministrator(
    playlistId: string,
    playlistItemId: string,
    localize?: boolean
  ): Promise<PlaylistItem> {
    // Set url
    let url: string = `/playlists/${playlistId}/items/${playlistItemId}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `?localize=${localize}`;
    }

    // Get playlist item from backend
    const response: AxiosResponse<PlaylistItem> =
      await BackendApiManager.instance.get<PlaylistItem>(url);

    // Return
    return response.data;
  }

  // Get playlists in the system by type as a system administrator
  public async getPlaylistsAsSystemAdministrator(
    skip: number,
    limit: number,
    type?: PlaylistType,
    nameSearch?: string,
    localize?: boolean
  ): Promise<{totalCount: number; data: Array<Playlist>}> {
    // Set url
    let url: string = `/playlists?skip=${skip}&limit=${limit}`;

    // Type
    if (type) {
      // Add to the url
      url += `&type=${type}`;
    }

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get playlists from backend
    const response: AxiosResponse<Array<Playlist>> =
      await BackendApiManager.instance.get<Array<Playlist>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get entities in the system associated with a playlist as a system administrator
  public async getPlaylistEntitiesAsSystemAdministrator(
    playlistId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<PlaylistEntity>}> {
    // Set url
    let url: string = `/playlists/${playlistId}/entities?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get entities from backend
    const response: AxiosResponse<Array<PlaylistEntity>> =
      await BackendApiManager.instance.get<Array<PlaylistEntity>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get entities in the system associated with a playlist item as a system administrator
  public async getPlaylistItemEntitiesAsSystemAdministrator(
    playlistId: string,
    playlistItemId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<PlaylistItemEntity>}> {
    // Set url
    let url: string = `/playlists/${playlistId}/items/${playlistItemId}/entities?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get entities from backend
    const response: AxiosResponse<Array<PlaylistItemEntity>> =
      await BackendApiManager.instance.get<Array<PlaylistItemEntity>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get playlist items in the system associated with a playlist as a system administrator
  public async getPlaylistItemsAsSystemAdministrator(
    playlistId: string,
    skip: number,
    limit: number,
    localize?: boolean,
    titleSearch?: string
  ): Promise<{totalCount: number; data: Array<PlaylistItem>}> {
    // Set url
    let url: string = `/playlists/${playlistId}/items?skip=${skip}&limit=${limit}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Check text search
    if (titleSearch && titleSearch.trim().length !== 0) {
      // Add to the url
      url += `&title=${titleSearch}`;
    }

    // Get items from backend
    const response: AxiosResponse<Array<PlaylistItem>> =
      await BackendApiManager.instance.get<Array<PlaylistItem>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get playlists by type associated with an entity as a system administrator
  public async getPlaylistsInEntityAsSystemAdministrator(
    entityId: string,
    type: PlaylistType,
    skip: number,
    limit: number,
    nameSearch?: string,
    localize?: boolean
  ): Promise<{totalCount: number; data: Array<Playlist>}> {
    // Set url
    let url: string = `/playlists/entities/${entityId}?skip=${skip}&limit=${limit}&type=${type}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Get playlists from backend
    const response: AxiosResponse<Array<Playlist>> =
      await BackendApiManager.instance.get<Array<Playlist>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get playlist items in an entity as a system administrator
  public async getPlaylistItemsInEntityAsSystemAdministrator(
    entityId: string,
    playlistId: string,
    skip: number,
    limit: number,
    nameSearch?: string,
    localize?: boolean
  ): Promise<{totalCount: number; data: Array<PlaylistItem>}> {
    // Set url
    let url: string = `/playlists/${playlistId}/items/entities/${entityId}?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Get playlist items from backend
    const response: AxiosResponse<Array<PlaylistItem>> =
      await BackendApiManager.instance.get<Array<PlaylistItem>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get playlists in an entity by type as a professional caregiver
  public async getPlaylistsInEntityAsAffiliatedForEntity(
    entityId: string,
    skip: number,
    limit: number,
    type: PlaylistType,
    localize?: boolean,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<Playlist>}> {
    // Set url
    let url: string = `/entities/${entityId}/playlists?skip=${skip}&limit=${limit}&type=${type}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get playlists from backend
    const response: AxiosResponse<Array<Playlist>> =
      await BackendApiManager.instance.get<Array<Playlist>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get a playlist by id for an entity as a professional caregiver
  public async getPlaylistByIdForEntityAsAffiliatedForEntity(
    entityId: string,
    playlistId: string,
    localize?: boolean
  ): Promise<Playlist> {
    // Set url
    let url: string = `/entities/${entityId}/playlists/${playlistId}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `?localize=${localize}`;
    }

    // Get playlist from backend
    const response: AxiosResponse<Playlist> = await BackendApiManager.instance.get<Playlist>(url);

    // Return
    return response.data;
  }

  // Get playlist items in an entity as a professional caregiver
  public async getPlaylistItemsInEntityAsAffiliatedForEntity(
    entityId: string,
    playlistId: string,
    skip: number,
    limit: number,
    localize?: boolean,
    titleSearch?: string
  ): Promise<{totalCount: number; data: Array<PlaylistItem>}> {
    // Set url
    let url: string = `/entities/${entityId}/playlists/${playlistId}/items?skip=${skip}&limit=${limit}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Check text search
    if (titleSearch && titleSearch.trim().length !== 0) {
      // Add to the url
      url += `&title=${titleSearch}`;
    }

    // Get playlist items from backend
    const response: AxiosResponse<Array<PlaylistItem>> =
      await BackendApiManager.instance.get<Array<PlaylistItem>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get playlists associated with a user in an entity by type as a professional caregiver and system administrator
  public async getPlaylistsForUserInEntity(
    userId: string,
    entityId: string,
    skip: number,
    limit: number,
    type: PlaylistType,
    localize?: boolean,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<Playlist>}> {
    // Set url
    let url: string = `/entities/${entityId}/users/${userId}/playlists?skip=${skip}&limit=${limit}&type=${type}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get playlists from backend
    const response: AxiosResponse<Array<Playlist>> =
      await BackendApiManager.instance.get<Array<Playlist>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all users in an entity associated with a playlist as a professional caregiver
  public async getPlaylistUsersInEntityAsAffiliatedForEntity(
    entityId: string,
    playlistId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<PlaylistUser>}> {
    // Set url
    let url: string = `/entities/${entityId}/playlists/${playlistId}/users?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get users from backend
    const response: AxiosResponse<Array<PlaylistUser>> =
      await BackendApiManager.instance.get<Array<PlaylistUser>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all users in an entity associated with a playlist item as a professional caregiver
  public async getPlaylistItemUsersInEntityAsAffiliatedForEntity(
    entityId: string,
    playlistId: string,
    playlistItemId: string,
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<PlaylistItemUser>}> {
    // Set url
    let url: string = `/entities/${entityId}/playlists/${playlistId}/items/${playlistItemId}/users?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get users from backend
    const response: AxiosResponse<Array<PlaylistItemUser>> =
      await BackendApiManager.instance.get<Array<PlaylistItemUser>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get a playlist item by id for an entity as a professional caregiver
  public async getPlaylistItemByIdForEntityAsAffiliatedForEntity(
    entityId: string,
    playlistId: string,
    playlistItemId: string,
    localize?: boolean
  ): Promise<PlaylistItem> {
    // Set url
    let url: string = `/entities/${entityId}/playlists/${playlistId}/items/${playlistItemId}`;

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `?localize=${localize}`;
    }

    // Get playlist item from backend
    const response: AxiosResponse<PlaylistItem> =
      await BackendApiManager.instance.get<PlaylistItem>(url);

    // Return
    return response.data;
  }

  // Get playlist items associated with a user in an entity as a professional caregiver
  public async getPlaylistItemsForUserInEntityAsAffiliatedForEntity(
    userId: string,
    entityId: string,
    playlistId: string,
    skip: number,
    limit: number,
    localize?: boolean,
    titleSearch?: string
  ): Promise<{totalCount: number; data: Array<PlaylistItem>}> {
    // Set url
    let url: string = `/entities/${entityId}/users/${userId}/playlists/${playlistId}/items?skip=${skip}&limit=${limit}`;

    // Check text search
    if (titleSearch && titleSearch.trim().length !== 0) {
      // Add to the url
      url += `&title=${titleSearch}`;
    }

    // Localize
    if (typeof localize === 'boolean') {
      // Add to the url
      url += `&localize=${localize}`;
    }

    // Get playlist items from backend
    const response: AxiosResponse<Array<PlaylistItem>> =
      await BackendApiManager.instance.get<Array<PlaylistItem>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Update the isActive property of a playlist item for a user as a professional caregiver
  public async updatePlaylistItemActiveStateForUserAsAffiliatedForEntity(
    userId: string,
    playlistId: string,
    playlistItemId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/users/${userId}/playlists/${playlistId}/items/${playlistItemId}/active/${flag}`
    );
  }

  // Update the isActive property of a playlist for an entity as a system administrator
  public async updatePlaylistActiveStateForEntityAsSystemAdministrator(
    entityId: string,
    playlistId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/playlists/${playlistId}/entities/${entityId}/active/${flag}`
    );
  }

  // Update the isActive property of a playlist item for an entity as a system administrator
  public async updatePlaylistItemActiveStateForEntityAsSystemAdministrator(
    entityId: string,
    playlistId: string,
    playlistItemId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/playlists/${playlistId}/items/${playlistItemId}/entities/${entityId}/active/${flag}`
    );
  }

  // Update the isActive property of a playlist for all users in an entity as a professional caregiver
  public async updatePlaylistActiveStateForAllUsersAsAffiliatedForEntity(
    entityId: string,
    playlistId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/entities/${entityId}/playlists/${playlistId}/active/${flag}`
    );
  }

  // Update the isActive property of a playlist item for all users in an entity as a professional caregiver
  public async updatePlaylistItemActiveStateForAllUsersAsAffiliatedForEntity(
    entityId: string,
    playlistId: string,
    playlistItemId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/entities/${entityId}/playlists/${playlistId}/items/${playlistItemId}/active/${flag}`
    );
  }

  // Update the isActive property of a playlist for a user as a professional caregiver and system administrator
  public async updatePlaylistActiveStateForUser(
    userId: string,
    playlistId: string,
    flag: boolean
  ): Promise<void> {
    await BackendApiManager.instance.patch<void>(
      `/users/${userId}/playlists/${playlistId}/active/${flag}`
    );
  }
}
